/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React, { useContext, useState } from 'react';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { css } from 'styled-components';
import {
  // MouseIconWrapper,
  simpleGridColumnOverride,
  baseOuter,
  textOverridesForBanners,
  outerCSS,
  wrapperCSS,
  hideOnMobile,
  theMobileContentCSS,
  SplitContainer,
  splitFirstImageCSS,
  splitSecondImage,
  splitSecondImageTextCSS,
  WrapperSectionHero,
  innerCSS,
  AnimatedWrapper,
  AnimationControlButtonContainer,
} from './styles/SectionHero.styles';
// import { ReactComponent as MouseIcon } from '../../../../images/icon/mouse.svg';
// import scrollToElementById from '../../../../helpers/scrollToElementById';
import { DirectionContext } from '../../../../contexts/DirectionContext';
import AnimationControlButton from '../../Elements/Animated/AnimationControlButton';
import SectionElementAnimated from '../../Elements/Animated/SectionElementAnimated';
import CtaButtonRow from '../../Elements/CtaButtons';
import RichText from '../../Elements/RichText';
import StyledLinkWithIcon from '../../Elements/LinkWithIcon';
// import fireButtonAndLinkClicksEvent from '../../../../helpers/fireButtonAndLinkClicksEvent';

interface SectionHeroContentProps {
  id: string;
  __typename: 'ContentfulSectionElementRichText';
  text: {
    raw: string;
    references: {
      contentful_id: string;
      gatsbyImageData: IGatsbyImageData;
      description: string;
      file: {
        url: string;
      };
    };
  };
  color?: string;
  textAlign: string;
}
interface ResponsiveImageCollectionProp {
  imageName: string;
  isMainImage: boolean;
  maxWidth: number;
  imageTitle: {
    title: string;
    file: {
      url: string;
    };
    description?: string;
  };
  secondImage?: {
    file: {
      url: string;
    };
  };
}

export interface SectionHeroProps {
  key?: string;
  id?: string;
  __typename?: string;
  name: string;
  textPosition: 'left' | 'center' | 'right' | 'ieLetsTalk';
  content: SectionHeroContentProps[];
  mirrorContent?: boolean;
  targetSectionIdForMouseIconButton?: string;
  responsiveImageCollection?: ResponsiveImageCollectionProp[];
  size: 'small' | 'large';
}

type ImageKeys = string | number;

const SectionHero = ({
  textPosition,
  content,
  mirrorContent,
  // targetSectionIdForMouseIconButton,
  responsiveImageCollection = [],
  size,
}: SectionHeroProps) => {
  const theHeroCSS = outerCSS[textPosition];
  const theContentCSS = innerCSS[textPosition];
  const theContentWrapper = wrapperCSS[textPosition];

  const direction = useContext(DirectionContext);
  const [isPlaying, setIsPlaying] = useState(true);

  let bgAltText = '';
  let bannerJSX = null;

  const images = responsiveImageCollection.map(respImage => {
    const container: Record<ImageKeys, any> = {};

    container['source'] = respImage.imageTitle.file.url;
    container['maxWidth'] = respImage.maxWidth;
    container['secondImageSource'] = respImage.secondImage?.file?.url ?? null;

    return container;
  });

  images.sort((a, b) => (b.maxWidth > a.maxWidth && 1) || -1);
  const bgImages = images.reduce((acc, css, idx) => {
    if (idx === 0) {
      return `background-image: url("${css.source}");`;
    }
    return `${acc}
        @media (max-width: ${css.maxWidth}px) {
          & {
            background-image: url("${css.source}");
            }
        };`;
  }, '');

  const hasAnimatedComponent = (content: any[]) => {
    return content.some(
      contentItem =>
        contentItem.__typename === 'ContentfulSectionElementAnimated'
    );
  };

  const getContent = (content: any[]) => {
    // Builds the content for a CONTENTFUL_SECTION_CTA.
    const ctaContent = content.map(content => {
      switch (content.__typename) {
        case 'ContentfulSectionElementCtaButtonRow':
          return (
            <CtaButtonRow
              key={content.id}
              buttonRowAlignment={content.buttonRowAlignment}
              isMobileViewButtonOrderReversed={
                content.isMobileViewButtonOrderReversed
              }
              buttons={content.buttons}
              spacing={5}
            />
          );

        case 'ContentfulSectionElementRichText':
          return (
            <RichText
              key={content.id}
              className="richText"
              text={content.text}
              color={content.color}
              textAlign={content.textAlign}
            />
          );

        case 'ContentfulSectionElementWs10LinkWithIcon':
          return (
            <StyledLinkWithIcon
              key={content.id}
              desktop={{
                label: content.desktopLabel,
                appearance: content.desktopAppearance,
                inverse: content.desktopInverse,
                underline: content.desktopUnderline,
              }}
              mobile={{
                label: content.mobileLabel,
                appearance: content.mobileAppearance,
                inverse: content.mobileInverse,
                underline: content.mobileUnderline,
              }}
              iconName={content.iconName}
              targetSectionId={content.targetSectionId}
            />
          );
        case 'ContentfulSectionElementAnimated':
          return <SectionElementAnimated {...content} isPlaying={isPlaying} />;
        default:
          return false;
      }
    });

    if (
      content.some(
        contentItem =>
          contentItem.__typename === 'ContentfulSectionElementAnimated'
      )
    ) {
      ctaContent.push(
        <AnimationControlButtonContainer>
          <AnimationControlButton
            isPlaying={isPlaying}
            onClick={() => setIsPlaying(current => !current)}
          />
        </AnimationControlButtonContainer>
      );
    }

    return ctaContent;
  };

  const theContent = getContent(content);

  // We assume the alt text for the responsive image set is the same for all of them, so take the first
  // one that is not "". If none are set, the alt text will be the initial value of "".
  responsiveImageCollection.some(imageData => {
    const isFound = imageData.imageTitle.description !== '';
    if (isFound) {
      bgAltText = imageData.imageTitle.description as string;
    }
    return isFound;
  });

  // function handleClick(targetSectionId: string, label: string) {
  //   fireButtonAndLinkClicksEvent({ event_label: label, event_action: "button click" });
  //   scrollToElementById(targetSectionId)
  // }

  if (images[0] && images[0].secondImageSource !== null) {
    // Render a slanted split banner.
    // NB - The secondImage is the split background.
    const splitSecondImageBgCSS = css`
      background-image: url(${images[0].secondImageSource});
    `;

    // Option to horizontal flip content
    const styles = {
      horizontalFlipContent: {
        transform: mirrorContent ? 'scaleX(-1)' : 'none',
      },
    };
    bannerJSX = (
      <SplitContainer
        style={styles.horizontalFlipContent}
        direction={direction}
      >
        <div
          role="img"
          aria-label={bgAltText}
          style={styles.horizontalFlipContent}
          css={[theHeroCSS, bgImages, splitFirstImageCSS]}
        ></div>
        <div
          css={[splitSecondImage, splitSecondImageBgCSS]}
          id="section-hero-container"
        >
          <div
            style={styles.horizontalFlipContent}
            css={[splitSecondImageTextCSS, textOverridesForBanners]}
          >
            {theContent}
          </div>
        </div>
      </SplitContainer>
    );
  } else if (hasAnimatedComponent(content)) {
    bannerJSX = <AnimatedWrapper>{theContent}</AnimatedWrapper>;
  } else {
    // render a standard banner.
    bannerJSX = (
      <React.Fragment>
        <div css={simpleGridColumnOverride}>
          <div
            role="img"
            aria-label={bgAltText}
            css={[baseOuter, textOverridesForBanners, bgImages]}
          >
            <div
              css={[theHeroCSS, theContentWrapper]}
              id="section-hero-container"
            >
              <div
                css={[theContentCSS, hideOnMobile]}
                id="section-hero-content"
              >
                {theContent}
              </div>
              {/* {targetSectionIdForMouseIconButton &&
                <MouseIconWrapper onClick={() => handleClick(targetSectionIdForMouseIconButton, 'mouse icon')}>
                  <MouseIcon />
                </MouseIconWrapper>
              } */}
            </div>
          </div>
          <div css={theMobileContentCSS}>{theContent}</div>
        </div>
      </React.Fragment>
    );
  }

  return <WrapperSectionHero size={size}>{bannerJSX}</WrapperSectionHero>;
};

export default SectionHero;
